import type { ReactNode } from "react";

import { useRef } from "react";

interface UploadDropZoneProps {
  accept: string;
  filterFiles?: (files: File[]) => File[];
  helperLine: ReactNode;
  icon: ReactNode;
  onFilesSelected: (files: File[]) => void;
  warningLine: ReactNode;
}

export function UploadDropZone({
  accept,
  filterFiles,
  helperLine,
  icon,
  onFilesSelected,
  warningLine,
}: UploadDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = [...(e.target.files ?? [])];

    if (selected.length === 0) return;

    onFilesSelected(selected);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const dropped = filterFiles
      ? filterFiles([...e.dataTransfer.files])
      : [...e.dataTransfer.files];

    if (dropped.length > 0) {
      onFilesSelected(dropped);
    }
  };

  return (
    <div
      className="flex w-[30%] shrink-0 cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border border-border-secondary bg-bg-primary p-6 text-center transition-colors hover:bg-bg-primary-hover"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border-secondary bg-bg-primary shadow-sm">
        {icon}
      </div>
      <div>
        <p className="text-sm">
          <span className="font-medium text-text-brand-primary">
            Click to upload
          </span>{" "}
          <span className="text-text-tertiary">or drag and drop</span>
        </p>
        <p className="mt-1 text-xs text-text-tertiary">{helperLine}</p>
        <p className="mt-1 text-xs font-semibold text-text-secondary">
          {warningLine}
        </p>
      </div>
      <input
        accept={accept}
        className="hidden"
        multiple
        onChange={handleFileChange}
        ref={inputRef}
        type="file"
      />
    </div>
  );
}
